import React, { useState } from "react";

import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";

import { BlackCrown, GoldCrown, PlanBadge } from "../../../Pages/Invitations/styles";
import {
	DiscountBadge,
	FeatureItem,
	FeatureList,
	PriceAmount,
	PricingCard,
	PricingHeader,
	PricingWrapper,
	SelectedIndicator
} from "./styles";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
	INVITATION_PRICING_PLANS_ENUM,
	Invitations,
	Price
} from "../../../store/services/invitations/dto/invitations.dto";
import { getDiscountAmount, getInvitationPrice } from "../../../utils/getInvitationPrice";
import { PRICING_PLAN_CONFIG } from "../../../constants";
import { DefaultPricingPlan, getFullInvitationsState } from "../../../store/slices/invitations/invitations.slice";
import { round } from "lodash";
import { useAppSelector } from "../../../store";
import { ExchangeRate } from "../../../store/services/payment/dto/payment.dto";

const FREE_PLAN: Price = {
	type: INVITATION_PRICING_PLANS_ENUM.FREE,
	name: "Free",
	amount: 0,
	discount: 0,
	is_discount_percentage: false,
	currency: "EGP"
};

const getUsdEquivalent = (amount: number, exchangeRate?: ExchangeRate): string | null => {
	if (!exchangeRate?.rate) return null;
	return `≈ ${formatCurrency(round(amount / exchangeRate.rate, 2), exchangeRate.base_currency)}`;
};

const PricingModal = ({
	open,
	onClose,
	onCheckout,
	data,
	exchangeRate
}: {
	open: boolean;
	onClose: () => void;
	onCheckout: (plan: Price) => void;
	data: Invitations;
	exchangeRate?: ExchangeRate;
}) => {
	const isFreeVisible = data.selectedPricingPlan === INVITATION_PRICING_PLANS_ENUM.FREE;
	const [selected, setSelected] = useState<INVITATION_PRICING_PLANS_ENUM>(data.selectedPricingPlan + 1);
	const pricing = data?.pricing.filter(p => p.type > data.selectedPricingPlan);
	const displayPlans = isFreeVisible ? [FREE_PLAN, ...pricing] : pricing;
	const { invitationPayedData } = useAppSelector(getFullInvitationsState);
	const selectedPlan = displayPlans.find(p => p.type === selected) || DefaultPricingPlan;
	const isFreeSelected = selected === INVITATION_PRICING_PLANS_ENUM.FREE;
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle sx={{ pb: 0, pt: 3, px: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Typography variant="h5" fontWeight={700} color="text.primary">
							Choose your plan
						</Typography>
						<Typography variant="body2" color="text.secondary" mt={0.5}>
							Select the plan that fits your special day
						</Typography>
					</Box>
					<IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
						<Close fontSize="small" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ px: 3, pt: 2 }}>
				<PricingWrapper>
					{displayPlans.map(plan => {
						const isSelected = selected === plan.type;
						const isFree = plan.type === INVITATION_PRICING_PLANS_ENUM.FREE;
						const _plan = PRICING_PLAN_CONFIG[plan.type];
						const { description, features } = _plan;
						const isSignature = plan.type === INVITATION_PRICING_PLANS_ENUM.SIGNATURE;
						const planFinalAmount = getInvitationPrice(
							plan?.amount - invitationPayedData.amount || 0,
							plan.is_discount_percentage,
							plan.discount
						);
						const planFinalPrice = isFree ? "Free" : formatCurrency(planFinalAmount, plan.currency);
						// const planUsdLabel = isFree ? null : getUsdEquivalent(planFinalAmount, exchangeRate);
						return (
							<PricingCard
								key={plan.type}
								$selected={isSelected}
								$signature={isSignature}
								onClick={() => setSelected(plan.type)}
							>
								{isSelected && (
									<SelectedIndicator $signature={isSignature}>
										<Check sx={{ fontSize: 14, color: "#fff" }} />
									</SelectedIndicator>
								)}

								{plan.discount > 0 && (
									<DiscountBadge>
										Save {getDiscountAmount(plan.discount, plan.currency, plan.is_discount_percentage)}
									</DiscountBadge>
								)}

								<PricingHeader>{isSignature ? <GoldCrown /> : <BlackCrown />}</PricingHeader>

								<PlanBadge $signature={isSignature}>{plan.name}</PlanBadge>
								{plan.discount > 0 && <Box position={"relative"} height={"20px"} />}
								<PriceAmount flexWrap={"wrap"}>
									{plan.discount > 0 && (
										<Typography variant={"caption"} className={"old_price"}>
											{formatCurrency(round(plan?.amount, 2), plan.currency)}
										</Typography>
									)}

									<Typography variant="h4" fontWeight={800} color={isSignature ? "#8B6914" : "primary.dark"}>
										{planFinalPrice}
									</Typography>

									<Typography variant="body2" color="text.secondary" sx={{ visibility: isFree ? "hidden" : "visible" }}>
										/ one-time
									</Typography>
								</PriceAmount>
								{/*<Typography*/}
								{/*	variant="body1"*/}
								{/*	color="text.secondary"*/}
								{/*	display="block"*/}
								{/*	mb={1}*/}
								{/*	sx={{ visibility: planUsdLabel ? "visible" : "hidden" }}*/}
								{/*>*/}
								{/*	{planUsdLabel || " "}*/}
								{/*</Typography>*/}
								<Typography variant="body2" color="text.secondary" lineHeight={1.5}>
									{description}
								</Typography>

								<FeatureList>
									{features.map((feature, i) => (
										<FeatureItem key={i}>
											<Check
												sx={{
													fontSize: 16,
													color: isSignature ? "#C9A84C" : "primary.main",
													flexShrink: 0,
													mt: "1px"
												}}
											/>
											<span>{feature}</span>
										</FeatureItem>
									))}
								</FeatureList>
							</PricingCard>
						);
					})}
				</PricingWrapper>

				<Box
					display="flex"
					justifyContent="flex-end"
					flexDirection={isSmallerScreen ? "column-reverse" : "row"}
					gap={1.5}
					pb={1}
				>
					<Button variant="outlined" color="inherit" onClick={onClose} sx={{ borderRadius: "8px", px: 3 }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={isFreeSelected}
						onClick={() => onCheckout(selectedPlan as Price)}
						sx={{
							borderRadius: "8px",
							px: 4,
							background: selected === INVITATION_PRICING_PLANS_ENUM.SIGNATURE ? "#C9A84C" : undefined,
							"&:hover": {
								background: selected === INVITATION_PRICING_PLANS_ENUM.SIGNATURE ? "#B8963E" : undefined
							}
						}}
					>
						{isFreeSelected
							? "Free plan selected"
							: `Get ${selectedPlan.name} — ${formatCurrency(
									getInvitationPrice(
										selectedPlan?.amount - invitationPayedData.amount || 0,
										selectedPlan.is_discount_percentage,
										selectedPlan.discount
									),
									selectedPlan.currency
								)}`}
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default PricingModal;
